import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { PlusCircle, MinusCircle, Share2, Star } from 'lucide-react';
import WatchlistAssignGroupsModal from './WatchlistAssignGroupsModal';
import SharePreviewModal from './SharePreviewModal';
import { saveContract, getWatchlistGroups, assignContractsToGroup } from '../services/api';
import ScreenerFilterControls from './ScreenerFilterControls';
import { SimulationSettingsPanel } from './shared';
import TickerHoverPreview from './DashboardBuilder/TickerHoverPreview';
import OptionsPremiumChartModal from './OptionsPremiumChartModal';
import { recalculateSimulation, getMaxDaysToTarget, getFractionalDaysToExpiry, INTRADAY_T_FLOOR_YEARS } from '../utils/blackScholes';
import { getRowKey, compactRows } from '../utils/rowKey';
import { getSimpleStrikePositionLabel } from '../utils/presetMappings';
import { getHeatmapCellColor, ALL_SCENARIOS } from '../utils/elasticityCalculations';
import { formatDataSourceTooltip } from '../utils/dataSourceTooltip';
import { useLastClickedTicker } from '../contexts/LastClickedTickerContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const COLUMN_GROUPS = {
  All: [
    "Ticker", "Position Type", "Option Type", "Strike", "Current Premium", "Expiration",
    "Strike Position",
    "Underlying Price", "Simulated Underlying", "Simulated Premium",
    "Days to Gain", "Underlying Gain %", "Premium % Gain",
    "Allocated Equity", "Simulated Equity",
    "Ann. Return (Target)", "Ann. Return (Expiry)",
    "% OTM/ITM", "Days Until Expiration", "Run Label",
    "Collateral Required", "Return on Collateral %", "Max Profit", "Max Loss", "Simulated P&L",
    "Delta", "Gamma", "Theta", "Vega", "Rho", "Implied Volatility",
    "Volume"
  ],
  Simulation: [
    "Strike Position", "Underlying Price", "Simulated Underlying", "Simulated Premium", "Underlying Gain %", "Days to Gain", "Premium % Gain", "Allocated Equity", "Simulated Equity", "Implied Volatility", "Ann. Return (Target)", "Ann. Return (Expiry)"
  ],
  "Sell Metrics": [
    "Underlying Price", "Collateral Required", "Return on Collateral %", "Days to Gain", "Simulated Underlying", "Simulated Premium", "Premium % Gain", "Simulated P&L", "Ann. Return (Target)", "Ann. Return (Expiry)", "Max Profit", "Max Loss"
  ],
  "Contract Details": [
    "Strike Position", "% OTM/ITM", "Days Until Expiration", "Allocated Equity", "Run Label"
  ],
  Greeks: [
    "Delta", "Gamma", "Theta", "Vega", "Rho", "Implied Volatility"
  ],
  "Market Data": [
    "Volume"
  ]
};

const ESSENTIAL_COLUMNS = [
  "Ticker", "Position Type", "Option Type", "Strike", "Expiration", "Current Premium"
];

// Column header tooltip descriptions
const COLUMN_HEADER_TOOLTIPS = {
  // Essential/Identity columns
  "Position Type": "Whether you are buying (long) or selling (short) this option contract.",
  "Option Type": "Call options profit when the stock rises. Put options profit when the stock falls.",
  "Strike": "The price at which you can buy (call) or sell (put) the underlying stock if exercised.",
  "Expiration": "The date the option contract expires. After this date, the option becomes worthless if not exercised.",
  "Current Premium": "The current market price (premium) of the option contract per share.",

  // Simulation columns
  "Underlying Price": "The current market price of the underlying stock.",
  "Simulated Underlying": "Projected underlying price after applying the simulated gain percentage.",
  "Simulated Premium": "Projected option premium after applying the simulated stock move and time decay.",
  "Days to Gain": "The holding period in days used for this simulation scenario.",
  "Underlying Gain %": "The simulated stock price change percentage used in this scenario.",
  "Premium % Gain": "Percentage change between current premium and the simulated premium.",
  "Ann. Return (Target)": "Annualized return if you hold until the exit date. Calculated as (% gain / days held) x 365.",
  "Ann. Return (Expiry)": "Annualized return if you hold until expiration.",
  "Allocated Equity": "Total capital that would be invested: based on allocation amount or contracts x premium x 100.",
  "Simulated Equity": "Projected total equity value based on the simulated premium.",

  // Contract details
  "Strike Position": "Whether the strike is in the money (ITM), at the money (ATM), or out of the money (OTM), and by how much.",
  "% OTM/ITM": "How far the strike is from the current stock price. Negative = ITM (in the money), Positive = OTM (out of the money).",
  "Days Until Expiration": "Number of calendar days remaining until the option contract expires.",
  "Run Label": "The label or scenario name from the screener parameters that generated this result.",

  // Sell metrics
  "Collateral Required": "Cash or margin required to hold a short (sell) position. For naked calls, this can be substantial.",
  "Return on Collateral %": "Percentage return relative to the collateral required, not the premium received.",
  "Max Profit": "Maximum possible profit for this position. For sold options, this is the premium received.",
  "Max Loss": "Maximum possible loss for this position. Naked calls have unlimited max loss.",
  "Simulated P&L": "Projected profit or loss in dollars based on your position size and the simulated premium.",

  // Greeks
  "Implied Volatility": "Market's expectation of future price movement. Higher IV means higher premiums and larger expected swings.",
  "Delta": "Sensitivity of option price to a $1 change in the underlying. Calls: 0 to 1, Puts: -1 to 0.",
  "Theta": "Daily time decay in dollars. Represents how much the option loses per day, all else equal.",
  "Gamma": "Rate of change of Delta per $1 move in the underlying. Higher near ATM and expiration.",
  "Vega": "Sensitivity of option price to a 1% change in implied volatility.",
  "Rho": "Sensitivity of option price to a 1% change in the risk-free interest rate.",
};

function ScreenerResultsTable({
  results,
  // Shared state from ScreenerResponsive (persists across view switches)
  favoritedRows: sharedFavoritedRows,
  setFavoritedRows: sharedSetFavoritedRows,
  removedRows: sharedRemovedRows,
  setRemovedRows: sharedSetRemovedRows,
  showFavoritesOnly: sharedShowFavoritesOnly,
  setShowFavoritesOnly: sharedSetShowFavoritesOnly,
  selectedExpirations: sharedSelectedExpirations,
  setSelectedExpirations: sharedSetSelectedExpirations,
  selectedTickers: sharedSelectedTickers,
  setSelectedTickers: sharedSetSelectedTickers,
  selectedStrikePositions: sharedSelectedStrikePositions,
  setSelectedStrikePositions: sharedSetSelectedStrikePositions,
  sharedDaysToExit,
  setSharedDaysToExit,
  defaultDays: sharedDefaultDays,
  daysToExitMode,
  setDaysToExitMode,
  daysToExitPercent,
  setDaysToExitPercent,
  scenarioDefaults,
  screenerMode = 'default',
  daysToExitUnit = 'days',
  setDaysToExitUnit,
  maxFractionalDays = null,
  scenarioBasePrice = null,
}) {
  // Fallback to local state if shared props not provided (backwards compatibility)
  const [localFavoritedRows, localSetFavoritedRows] = useState(new Set());
  const [localRemovedRows, localSetRemovedRows] = useState(new Set());
  const [localShowFavoritesOnly, localSetShowFavoritesOnly] = useState(false);
  const [localSelectedExpirations, localSetSelectedExpirations] = useState([]);
  const [localSelectedTickers, localSetSelectedTickers] = useState([]);
  const [localSelectedStrikePositions, localSetSelectedStrikePositions] = useState([]);

  const favoritedRows = sharedFavoritedRows ?? localFavoritedRows;
  const setFavoritedRows = sharedSetFavoritedRows ?? localSetFavoritedRows;
  const removedRows = sharedRemovedRows ?? localRemovedRows;
  const setRemovedRows = sharedSetRemovedRows ?? localSetRemovedRows;
  const showFavoritesOnly = sharedShowFavoritesOnly ?? localShowFavoritesOnly;
  const setShowFavoritesOnly = sharedSetShowFavoritesOnly ?? localSetShowFavoritesOnly;
  const selectedExpirations = sharedSelectedExpirations ?? localSelectedExpirations;
  const setSelectedExpirations = sharedSetSelectedExpirations ?? localSetSelectedExpirations;
  const selectedTickers = sharedSelectedTickers ?? localSelectedTickers;
  const setSelectedTickers = sharedSetSelectedTickers ?? localSetSelectedTickers;
  const selectedStrikePositions = sharedSelectedStrikePositions ?? localSelectedStrikePositions;
  const setSelectedStrikePositions = sharedSetSelectedStrikePositions ?? localSetSelectedStrikePositions;

  const [selectedGroup, setSelectedGroup] = useState("Simulation");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Simulation settings state
  const [simulationActive, setSimulationActive] = useState(false);
  const [daysToTarget, setDaysToTarget] = useState(null);
  const [underlyingGain, setUnderlyingGain] = useState(null);
  const [originalResults] = useState(results); // Preserve original data

  // Auto-set simulation defaults from queue scenario params
  useEffect(() => {
    if (scenarioDefaults) {
      const days = scenarioDefaults.daysToGain || 45;
      const gain = scenarioDefaults.stockGainPct || 10;
      setDaysToTarget(days);
      setUnderlyingGain(gain);
      setSimulationActive(true);
    }
  }, [scenarioDefaults]);

  // Premium chart modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Last clicked ticker context for dynamic widget updates
  const { setLastClickedTicker, setLastClickedContract } = useLastClickedTicker();

  // Watchlist save modal state
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistGroups, setWatchlistGroups] = useState([]);
  const [savedContractId, setSavedContractId] = useState(null);
  const [savedContractDetails, setSavedContractDetails] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingRowIndex, setSavingRowIndex] = useState(null);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Handler for clicking on options contract to open options dashboard
  const handleContractClick = (row) => {
    try {
      const ticker = row['Ticker'];
      const optionType = row['Option Type']?.toLowerCase();
      const strike = row['Strike'];
      const expiration = row['Expiration'];

      if (!ticker || !optionType || !strike || !expiration) {
        console.warn('Missing contract details for options dashboard');
        return;
      }

      // Update context for dynamic widgets
      setLastClickedTicker(ticker);
      setLastClickedContract({
        option_type: optionType,
        strike: parseFloat(strike),
        expiration: expiration
      });

      // Navigate to options dashboard in new tab
      const url = `/options-dashboard/${ticker}/${optionType}/${strike}/${expiration}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error handling contract click:', error);
    }
  };

  // Handler for clicking on premium to open chart modal (charts view)
  const handlePremiumClick = (row) => {
    try {
      const ticker = row['Ticker'];
      const optionType = row['Option Type']?.toLowerCase();
      const strike = row['Strike'];
      const expiration = row['Expiration'];

      if (!ticker || !optionType || !strike || !expiration) {
        console.warn('Missing contract details for premium chart');
        return;
      }

      // Look up full row from sortedData for simulated returns tab
      const fullRow = sortedData.find(r =>
        r['Ticker'] === row['Ticker'] &&
        r['Option Type'] === row['Option Type'] &&
        r['Strike'] === row['Strike'] &&
        r['Expiration'] === row['Expiration']
      );

      setSelectedContract({
        ticker,
        optionType,
        strike: parseFloat(strike),
        expiration,
        rowData: fullRow || null,
        defaultView: 'charts',
      });
      setChartModalOpen(true);
    } catch (error) {
      console.error('Error handling premium click:', error);
    }
  };

  // Handler for saving contract to watchlist
  const handleSaveToWatchlist = async (row, rowIndex) => {
    try {
      setIsSaving(true);
      setSavingRowIndex(rowIndex);

      // Map screener fields to SavedContract fields
      const contractData = {
        ticker: row['Ticker'],
        option_type: row['Option Type']?.toLowerCase(),
        strike: parseFloat(row['Strike']),
        expiration: row['Expiration'],
        average_cost_per_contract: parseFloat(row['Current Premium']) || undefined,
        days_to_gain: row['Days to Gain'] ? parseInt(row['Days to Gain']) : undefined,
        number_of_contracts: 1
      };

      // Save contract to API
      const response = await saveContract(contractData);
      const newContractId = response.data.id;
      setSavedContractId(newContractId);

      // Store contract details for modal display
      setSavedContractDetails({
        ticker: row['Ticker'],
        strike: row['Strike'],
        expiration: row['Expiration'],
        numberOfContracts: 1
      });

      // Fetch groups for modal
      const groupsResponse = await getWatchlistGroups();
      setWatchlistGroups(groupsResponse.data || []);

      // Open modal
      setShowWatchlistModal(true);
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Failed to save contract: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
      setSavingRowIndex(null);
    }
  };

  // getRowKey + compactRows are imported from ../utils/rowKey (null-safe; shared
  // with the watchlist table and both heatmap views).
  const handleRemoveRow = (row) => {
    const key = getRowKey(row);
    setRemovedRows(prev => new Set([...prev, key]));
    // Also remove from favorites to keep count accurate
    setFavoritedRows(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const handleToggleFavorite = (row) => {
    const key = getRowKey(row);
    setFavoritedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Handler for assigning saved contract to groups
  const handleAssignToGroups = async (groupIds, mode) => {
    if (!savedContractId || groupIds.length === 0) return;

    try {
      await assignContractsToGroup(groupIds, [savedContractId], mode);
    } catch (error) {
      console.error('Error assigning to groups:', error);
    }
  };

  // Refresh groups (for when new group is created in modal)
  const fetchWatchlistGroups = async () => {
    try {
      const response = await getWatchlistGroups();
      setWatchlistGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const visibleColumns = useMemo(() => {
    if (selectedGroup === "All") return COLUMN_GROUPS.All;
    const groupCols = COLUMN_GROUPS[selectedGroup] || [];
    return [...ESSENTIAL_COLUMNS, ...groupCols.filter(col => !ESSENTIAL_COLUMNS.includes(col))];
  }, [selectedGroup]);

  // Apply simulation recalculation if active
  const processedData = useMemo(() => {
    // Compact at the source: drop null/undefined/non-object entries (stale cache,
    // partial payload) so a bad row can never reach the favorites sort comparator,
    // the filter predicates, or the cell render.
    const cleanResults = compactRows(results);
    if (!simulationActive || daysToTarget === null || underlyingGain === null) {
      return cleanResults;
    }

    // Recalculate simulations for all rows
    const isIntraday = screenerMode === 'intraday';
    return cleanResults.map(row => {
      // Intraday: fractional time-to-expiry (sub-day) + finer near-expiry floor.
      const fdte = isIntraday ? getFractionalDaysToExpiry(row['Expiration']) : null;
      const intradayCtx = isIntraday && Number.isFinite(fdte)
        ? { timeToExpiryDays: fdte, tFloorYears: INTRADAY_T_FLOOR_YEARS }
        : null;
      // Percentage mode: compute per-row days from each contract's expiration
      const effectiveDays = daysToExitMode === 'percent' && daysToExitPercent
        ? (intradayCtx
            ? fdte * daysToExitPercent
            : Math.max(1, Math.round(parseFloat(row['Days Until Expiration']) * daysToExitPercent)))
        : daysToTarget;
      return recalculateSimulation(row, effectiveDays, underlyingGain, 0.05, null, intradayCtx);
    });
  }, [results, simulationActive, daysToTarget, underlyingGain, daysToExitMode, daysToExitPercent, screenerMode]);

  const sortedData = useMemo(() => {
    const sortable = [...processedData];
    sortable.sort((a, b) => {
      // Tier 1: Favorites always float to top
      const aFav = favoritedRows.has(getRowKey(a)) ? 0 : 1;
      const bFav = favoritedRows.has(getRowKey(b)) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;

      // Tier 2a: Intraday default order (no column selected) = options-chain:
      // Ticker → Expiration → Strike ascending. Other modes keep backend order.
      if (!sortConfig.key && screenerMode === 'intraday') {
        const aTicker = String(a['Ticker'] || '').toLowerCase();
        const bTicker = String(b['Ticker'] || '').toLowerCase();
        if (aTicker < bTicker) return -1;
        if (aTicker > bTicker) return 1;
        const aExp = String(a['Expiration'] || '');
        const bExp = String(b['Expiration'] || '');
        if (aExp < bExp) return -1;
        if (aExp > bExp) return 1;
        const aStrike = parseFloat(a['Strike']) || 0;
        const bStrike = parseFloat(b['Strike']) || 0;
        return aStrike - bStrike;
      }

      // Tier 2: Normal sort by selected column
      if (sortConfig.key) {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        // Date columns: compare as strings (YYYY-MM-DD sorts chronologically)
        if (sortConfig.key === 'Expiration') {
          const aStr = String(aVal || '');
          const bStr = String(bVal || '');
          if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sortable;
  }, [processedData, sortConfig, favoritedRows, screenerMode]);

  const filteredData = useMemo(() => {
    const withVisibleColumns = sortedData.map(row => {
      const filteredRow = {};
      visibleColumns.forEach(col => {
        filteredRow[col] = row[col];
      });
      // Preserve _meta for tooltips
      if (row._meta) {
        filteredRow._meta = row._meta;
      }
      // Preserve Strike Position for filtering even when column not displayed
      if (row["Strike Position"] !== undefined) {
        filteredRow["Strike Position"] = row["Strike Position"];
      }
      return filteredRow;
    });

    return withVisibleColumns.filter(row => {
      const rowKey = getRowKey(row);
      return (
        (selectedExpirations.length === 0 || selectedExpirations.includes(row["Expiration"])) &&
        (selectedTickers.length === 0 || selectedTickers.includes(row["Ticker"])) &&
        (selectedStrikePositions.length === 0 || selectedStrikePositions.includes(row["Strike Position"])) &&
        !removedRows.has(rowKey) &&
        (!showFavoritesOnly || favoritedRows.has(rowKey))
      );
    });
  }, [sortedData, visibleColumns, selectedExpirations, selectedTickers, selectedStrikePositions, removedRows, showFavoritesOnly, favoritedRows]);

  // Calculate max days to target from filtered data
  const getMaxDaysFromFilteredData = () => {
    // Use filtered data if filters are active, otherwise use all sorted data
    const dataToUse = (selectedExpirations.length > 0 || selectedTickers.length > 0)
      ? filteredData
      : sortedData;

    if (!dataToUse || dataToUse.length === 0) return { days: 365, date: null };

    // Extract expiration dates and calculate days from today
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    const expirationsWithDays = dataToUse
      .map(row => row['Expiration'])
      .filter(expDate => expDate && expDate !== '-')
      .map(expDate => {
        // Parse the expiration date (assuming format: "YYYY-MM-DD" or "MM/DD/YYYY")
        const expiration = new Date(expDate);
        expiration.setHours(0, 0, 0, 0);

        // Calculate days difference
        const diffTime = expiration - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return { days: diffDays, date: expDate };
      })
      .filter(item => item.days > 0) // Only include future expirations
      .sort((a, b) => a.days - b.days); // Sort by days ascending

    if (expirationsWithDays.length === 0) return { days: 365, date: null };

    // Return the minimum (nearest expiration) with its date
    return {
      days: expirationsWithDays[0].days,
      date: expirationsWithDays[0].date
    };
  };

  // Get default days and gain from scenario params or original results
  const getDefaultSimulationValues = () => {
    // Prefer scenario defaults from queue if available
    if (scenarioDefaults) {
      return {
        days: scenarioDefaults.daysToGain || 45,
        gain: scenarioDefaults.stockGainPct || 10,
      };
    }
    if (!originalResults || originalResults.length === 0) {
      return { days: 45, gain: 10 };
    }
    // Use the first row's values as defaults
    const firstRow = originalResults[0];
    return {
      days: firstRow['Days to Gain'] || 45,
      gain: firstRow['Underlying Gain %'] || 10
    };
  };

  const handleSimulationRefresh = (days, gain) => {
    setDaysToTarget(days);
    setUnderlyingGain(gain);
    setSimulationActive(true);
    // Sync shared days so heatmap view stays in sync (only in fixed mode)
    if (daysToExitMode !== 'percent' && setSharedDaysToExit) {
      setSharedDaysToExit(days);
      if (setDaysToExitMode) setDaysToExitMode('fixed');
    }
  };

  const handleSimulationReset = () => {
    setDaysToTarget(null);
    setUnderlyingGain(null);
    setSimulationActive(false);
    // Reset to per-contract mode
    if (setSharedDaysToExit) setSharedDaysToExit(null);
    if (setDaysToExitMode) setDaysToExitMode('per-contract');
    if (setDaysToExitPercent) setDaysToExitPercent(null);
  };

  const handleSort = (column) => {
    if (sortConfig.key === column) {
      setSortConfig({
        key: column,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setSortConfig({ key: column, direction: 'asc' });
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  // Determine which columns should be sticky (first 6 essential columns)
  const getStickyClass = (col, index) => {
    const stickyColumns = ['Ticker', 'Position Type', 'Option Type', 'Strike', 'Expiration', 'Current Premium'];
    const stickyIndex = stickyColumns.indexOf(col);
    if (stickyIndex !== -1 && visibleColumns.includes(col)) {
      const actualIndex = visibleColumns.indexOf(col);
      if (actualIndex < 6) {
        return `table-header-cell-sticky table-header-cell-sticky-${actualIndex + 1}`;
      }
    }
    return 'table-header-cell';
  };

  const getCellStickyClass = (col, index) => {
    const stickyColumns = ['Ticker', 'Position Type', 'Option Type', 'Strike', 'Expiration', 'Current Premium'];
    const stickyIndex = stickyColumns.indexOf(col);
    if (stickyIndex !== -1 && visibleColumns.includes(col)) {
      const actualIndex = visibleColumns.indexOf(col);
      if (actualIndex < 6) {
        return `table-cell-sticky table-cell-sticky-${actualIndex + 1}`;
      }
    }
    return 'table-cell';
  };

  // Format numbers for better display
  const formatValue = (value, column) => {
    if (value === undefined || value === null || value === 'undefined') {
      return '-';
    }

    // Special handling for "Unlimited" max loss (naked calls)
    if (column === 'Max Loss' && value === 'Unlimited') {
      return '⚠️ Unlimited';
    }

    // Check if it's a percentage column
    if (column.includes('% Gain') || column.includes('% OTM') || column === 'Return on Collateral %' || column.startsWith('Ann. Return')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num.toFixed(2)}%`;
      }
    }

    // Strike column - format cleanly without unnecessary decimals
    if (column === 'Strike') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        // Show decimals only if needed (e.g., 340 vs 340.50)
        return num % 1 === 0 ? `$${num.toFixed(0)}` : `$${num.toFixed(2)}`;
      }
    }

    // Check if it's a currency column (including new sell-specific columns)
    if (column.includes('Premium') || column.includes('Price') || column.includes('Equity') ||
        column === 'Simulated Underlying' || column === 'Collateral Required' ||
        column === 'Max Profit' || column === 'Max Loss' || column === 'Simulated P&L') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        // Format large numbers with commas
        if (Math.abs(num) >= 1000) {
          return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return `$${num.toFixed(2)}`;
      }
    }

    // Intraday hours/30-min holds make "Days to Gain" a fractional day value —
    // round to 2 decimals so it doesn't render with a long float tail. Other
    // modes / Days mode skip this and keep the existing display.
    if (column === 'Days to Gain' && screenerMode === 'intraday' && daysToExitUnit !== 'days') {
      const n = parseFloat(value);
      if (!isNaN(n)) return n.toFixed(2);
    }

    // Format large numbers
    const num = parseFloat(value);
    if (!isNaN(num) && Math.abs(num) >= 10000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }

    return value;
  };

  const getValueClass = (value, column) => {
    // Premium % Gain, Return on Collateral %, and Annualized Returns - positive is good, negative is bad
    if (column.includes('% Gain') || column === 'Return on Collateral %' || column.startsWith('Ann. Return') || column === 'Simulated P&L') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num > 0 ? 'table-cell-positive' : num < 0 ? 'table-cell-negative' : '';
      }
    }
    // Max Loss - "Unlimited" should be styled as a warning
    if (column === 'Max Loss' && value === 'Unlimited') {
      return 'text-orange-400 font-bold';
    }
    return '';
  };

  // Get data source tooltip for cells
  // Format: "P. 12/12/2025 10:54AM ET" or "E. 12/12/2025" or "def.", plus
  // provenance when the backend sends it, e.g. "P. calc. from mid <time> (delayed)"
  // (shared formatter in utils/dataSourceTooltip, also used by WatchlistTable)
  const getDataSourceTooltip = (row, column) => {
    const meta = row._meta;
    if (!meta) return null;

    // Map column names to metadata keys
    const metaMap = {
      'Underlying Price': 'underlying_price',
      'Current Premium': 'current_premium',
      'Implied Volatility': 'implied_volatility',
      'Delta': 'greeks',
      'Gamma': 'greeks',
      'Theta': 'greeks',
      'Vega': 'greeks',
      'Rho': 'greeks',
      'Volume': 'volume',
    };

    const metaKey = metaMap[column];
    if (!metaKey || !meta[metaKey]) return null;

    // Unknown source (e.g. cached results from before _meta was added) -> null, no tooltip
    return formatDataSourceTooltip(meta[metaKey], metaKey);
  };

  // Format column headers to be more readable and allow wrapping
  const formatColumnHeader = (column) => {
    const replacements = {
      'Underlying Price': 'Current\nUnderlying',
      'Current Premium': 'Current\nPrem',
      'Simulated Underlying': 'Sim\nUnderlying',
      'Simulated Premium': 'Sim\nPremium',
      'Underlying Gain %': 'Underlying\nGain %',
      'Premium % Gain': 'Premium\nGain %',
      'Days to Gain': 'Days To\nExit',
      'Allocated Equity': 'Allocated\nEquity',
      'Simulated Equity': 'Sim\nEquity',
      'Strike Position': 'Strike\nPosition',
      '% OTM/ITM': '% OTM/\nITM',
      'Days Until Expiration': 'Days Until\nExp',
      'Option Type': 'Type',
      'Position Type': 'Position',
      'Implied Volatility': 'Implied\nVol',
      // New sell-specific columns
      'Collateral Required': 'Collateral\nReq',
      'Return on Collateral %': 'Return on\nCollateral %',
      'Max Profit': 'Max\nProfit',
      'Max Loss': 'Max\nLoss',
      'Ann. Return (Target)': 'Ann. Ret.\n(Exit)',
      'Ann. Return (Expiry)': 'Ann. Ret.\n(Expiry)',
      'Simulated P&L': 'Sim\nP&L'
    };

    return replacements[column] || column;
  };

  // Get maximum width for specific columns
  const getColumnMaxWidth = (column) => {
    const narrowColumns = ['Ticker', 'Option Type', 'Delta', 'Gamma', 'Theta', 'Vega', 'Rho'];
    const mediumColumns = ['Position Type', 'Days to Gain', 'Volume', '% OTM/ITM', 'Strike Position', 'Max Profit', 'Max Loss', 'Simulated P&L', 'Ann. Return (Target)', 'Ann. Return (Expiry)'];
    const wideColumns = ['Current Premium', 'Simulated Premium', 'Underlying Price', 'Simulated Underlying', 'Allocated Equity', 'Simulated Equity', 'Collateral Required', 'Return on Collateral %', 'Expiration'];

    if (column === 'Strike') return '130px';
    if (narrowColumns.includes(column)) return '85px';
    if (mediumColumns.includes(column)) return '100px';
    if (wideColumns.includes(column)) return '120px';
    return '110px';
  };

  if (!Array.isArray(results) || results.length === 0) return null;

  const defaultValues = getDefaultSimulationValues();
  const maxDaysData = getMaxDaysFromFilteredData();

  return (
    <div className="mt-10">
      {/* Simulation Settings */}
      <div data-tour="simulation-settings">
      <SimulationSettingsPanel
        maxDays={maxDaysData.days}
        maxExpirationDate={maxDaysData.date}
        onRefresh={handleSimulationRefresh}
        onReset={handleSimulationReset}
        isActive={simulationActive}
        defaultDays={defaultValues.days}
        defaultGain={defaultValues.gain}
        contractCount={filteredData.length}
        label="All"
        hotButtons={screenerMode === 'intraday' ? ALL_SCENARIOS : undefined}
        gainStep={screenerMode === 'intraday' ? 0.5 : 1}
        showUnitToggle={screenerMode === 'intraday'}
        daysToExitUnit={daysToExitUnit}
        onUnitChange={setDaysToExitUnit}
        maxFractionalDays={maxFractionalDays}
        scenarioBasePrice={scenarioBasePrice}
        externalDays={sharedDaysToExit ?? null}
        daysToExitMode={daysToExitMode}
        daysToExitPercent={daysToExitPercent}
        onModeChange={(mode) => {
          if (setDaysToExitMode) setDaysToExitMode(mode);
        }}
        onPercentChange={(pct) => {
          if (setDaysToExitMode) setDaysToExitMode('percent');
          if (setDaysToExitPercent) setDaysToExitPercent(pct);
          if (setSharedDaysToExit) setSharedDaysToExit(null);
        }}
      />
      </div>

      {/* Filters */}
      <ScreenerFilterControls
        data={results}
        selectedExpirations={selectedExpirations}
        setSelectedExpirations={setSelectedExpirations}
        selectedTickers={selectedTickers}
        setSelectedTickers={setSelectedTickers}
        selectedStrikePositions={selectedStrikePositions}
        setSelectedStrikePositions={setSelectedStrikePositions}
        dataTour="results-filters"
      />

      {/* Group Tabs + Export CSV */}
      <div className="flex flex-wrap justify-between items-center mb-4">
        <div className="flex flex-wrap gap-2" data-tour="column-tabs">
          {Object.keys(COLUMN_GROUPS)
            .filter(group => group !== "Essentials")
            .map(group => (
              <button
                key={group}
                onClick={() => setSelectedGroup(group)}
                className={`tab-button ${selectedGroup === group ? 'tab-selected' : 'tab-unselected'}`}
              >
                {group}
              </button>
          ))}
        </div>

        <div className="flex gap-2 items-center">
          <button
            onClick={() => setShowFavoritesOnly(prev => !prev)}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
            title={showFavoritesOnly ? "Show all results" : "Show favorites only"}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>

          <button
            onClick={() => setShareModalOpen(true)}
            className="btn-export-primary flex items-center gap-1"
            title="Share as image"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>

          <CSVLink
            data={filteredData}
            headers={visibleColumns.map(col => ({ label: col, key: col }))}
            filename={"screener_filtered_optionsLeveling.csv"}
            className="btn-export-primary"
          >
            Export Filtered CSV
          </CSVLink>

          <CSVLink
            data={results}
            headers={COLUMN_GROUPS["All"].map(col => ({ label: col, key: col }))}
            filename={"screener_full_optionsLeveling.csv"}
            className="btn-export-secondary"
          >
            Export Full CSV
          </CSVLink>
        </div>
      </div>

      {/* Table */}
      <div className="table-container" data-tour="detail-table">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                {/* Sticky action buttons column header */}
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {visibleColumns.map((col, index) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className={getStickyClass(col, index)}
                    style={{ maxWidth: getColumnMaxWidth(col) }}
                    {...(COLUMN_HEADER_TOOLTIPS[col] ? {
                      'data-tooltip-id': 'screener-header-tooltip',
                      'data-tooltip-content': COLUMN_HEADER_TOOLTIPS[col],
                    } : {})}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-center whitespace-pre-line">{formatColumnHeader(col)}</span>
                      <span className="mt-1">{getSortIcon(col)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredData.map((row, idx) => (
                <tr
                  key={idx}
                  className={`${idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}${favoritedRows.has(getRowKey(row)) ? ' table-row-favorited' : ''}`}
                >
                  {/* Sticky action buttons cell (★ favorite / + save / - remove) */}
                  <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px 2px' }}>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleToggleFavorite(row)}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20
                                   flex items-center justify-center transition-all duration-200 group"
                        title={favoritedRows.has(getRowKey(row)) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Star
                          className={`w-3 h-3 transition-colors duration-200 ${
                            favoritedRows.has(getRowKey(row))
                              ? 'text-yellow-400'
                              : 'text-gray-400 group-hover:text-yellow-400'
                          }`}
                          fill={favoritedRows.has(getRowKey(row)) ? 'currentColor' : 'none'}
                        />
                      </button>
                      <button
                        onClick={() => handleSaveToWatchlist(row, idx)}
                        disabled={isSaving && savingRowIndex === idx}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#218aff] hover:bg-[#218aff]/20
                                   flex items-center justify-center transition-all duration-200 group disabled:opacity-50"
                        title="Save to Watchlist"
                      >
                        {isSaving && savingRowIndex === idx ? (
                          <span className="w-2.5 h-2.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <PlusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#218aff]" />
                        )}
                      </button>
                      <button
                        onClick={() => handleRemoveRow(row)}
                        className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20
                                   flex items-center justify-center transition-all duration-200 group"
                        title="Remove from results"
                      >
                        <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
                      </button>
                    </div>
                  </td>
                  {visibleColumns.map((col, i) => {
                    const value = row[col];
                    const formattedValue = formatValue(value, col);
                    const valueClass = getValueClass(value, col);
                    const cellClass = getCellStickyClass(col, i);
                    const isNumber = !isNaN(parseFloat(value)) && col !== 'Ticker' && col !== 'Option Type' && col !== 'Expiration';
                    const tooltip = getDataSourceTooltip(row, col);

                    // Heatmap-style background for Premium % Gain — graduated intensity by magnitude.
                    // Skip the green/red text class for this column so the colors don't conflict.
                    const isPremiumGainColumn = col === 'Premium % Gain';
                    const heatmapStyle = isPremiumGainColumn
                      ? getHeatmapCellColor(parseFloat(value), 'premiumGain')
                      : null;

                    const finalClassName = `${cellClass} ${isPremiumGainColumn ? '' : valueClass} ${isNumber ? 'table-cell-number' : ''}`;
                    const finalStyle = {
                      ...(tooltip ? { cursor: 'help' } : {}),
                      ...(heatmapStyle || {}),
                    };

                    return (
                      <td
                        key={i}
                        className={finalClassName}
                        title={tooltip || undefined}
                        style={Object.keys(finalStyle).length ? finalStyle : undefined}
                      >
                        {col === 'Ticker' ? (
                          <button
                            onClick={() => handlePremiumClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition font-medium flex items-center gap-1"
                            title="View Premium & Underlying Charts"
                          >
                            {formattedValue} ↗
                          </button>
                        ) : col === 'Current Premium' ? (
                          <button
                            onClick={() => handleContractClick(row)}
                            className="text-white hover:text-[#218aff] hover:underline transition flex items-center gap-1"
                            title="Open Options Dashboard"
                          >
                            {formattedValue} ↗
                          </button>
                        ) : col === 'Underlying Price' ? (
                          <Link
                            to={`/dashboard/${row['Ticker']}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-[#218aff] hover:underline transition"
                            title="Open Ticker Dashboard"
                          >
                            {formattedValue} ↗
                          </Link>
                        ) : col === 'Strike Position' ? (
                          row["Strike Position"] !== undefined
                            ? getSimpleStrikePositionLabel(row["Strike Position"], row["Option Type"])
                            : '-'
                        ) : (
                          Array.isArray(formattedValue) ? formattedValue.join(', ') : String(formattedValue)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unified Chart + Simulated Returns Modal */}
      <OptionsPremiumChartModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        ticker={selectedContract?.ticker}
        optionType={selectedContract?.optionType}
        strike={selectedContract?.strike}
        expiration={selectedContract?.expiration}
        rowData={selectedContract?.rowData}
        defaultView={selectedContract?.defaultView || 'charts'}
      />

      {/* Watchlist Assignment Modal */}
      <WatchlistAssignGroupsModal
        isOpen={showWatchlistModal}
        onClose={() => {
          setShowWatchlistModal(false);
          setSavedContractId(null);
          setSavedContractDetails(null);
        }}
        onAssign={handleAssignToGroups}
        groups={watchlistGroups}
        fetchGroups={fetchWatchlistGroups}
        contracts={savedContractId ? [savedContractId] : []}
        contractDetails={savedContractDetails ? [savedContractDetails] : []}
      />

      {/* Share Preview Modal */}
      <SharePreviewModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        data={filteredData}
        simulationMode="custom"
        daysToTarget={daysToTarget || getDefaultSimulationValues().days}
        underlyingGain={underlyingGain || getDefaultSimulationValues().gain}
      />

      {/* Column Header Tooltips */}
      <ReactTooltip
        id="screener-header-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default ScreenerResultsTable;
